import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
    isRememberedRenderOrigin,
    isTrustedRenderDestination,
    rememberRenderOrigin,
    sanitizeRenderHref,
} from "../lib/renderSafety.js";
import OverlayDialog from "./OverlayDialog.jsx";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";

const LINK_CLASS =
    "underline decoration-[color:var(--line-strong)] underline-offset-2 hover:decoration-[color:var(--ink)]";

export function openRenderDestination(value) {
    const href = sanitizeRenderHref(value);
    if (!href || typeof globalThis.window === "undefined") return false;
    const opened = globalThis.window.open(href, "_blank", "noopener,noreferrer");
    if (opened) opened.opener = null;
    return true;
}

export default function SafeRenderLink({
    href,
    children,
    trustedDocumentationHosts,
}) {
    const destination = sanitizeRenderHref(href);
    const url = destination ? new URL(destination) : null;
    const origin =
        url && (url.protocol === "http:" || url.protocol === "https:")
            ? url.origin
            : "";
    const trusted = destination
        ? isTrustedRenderDestination(destination, {
              documentationHosts: trustedDocumentationHosts,
          })
        : false;
    const remembered = !!origin && isRememberedRenderOrigin(origin);
    const liveHref =
        destination && (trusted || remembered)
            ? sanitizeRenderHref(destination)
            : undefined;
    const [pending, setPending] = useState(false);
    const [remember, setRemember] = useState(false);

    useEffect(() => {
        setPending(false);
        setRemember(false);
    }, [destination]);

    if (!destination) return <span>{children}</span>;

    const discloseOrOpen = () => {
        if (origin && isRememberedRenderOrigin(origin)) {
            openRenderDestination(destination);
            return;
        }
        setPending(true);
    };
    const onClick = (event) => {
        if (liveHref) return;
        event.preventDefault();
        discloseOrOpen();
    };
    const onAuxClick = (event) => {
        if (liveHref || event.button !== 1) return;
        event.preventDefault();
        discloseOrOpen();
    };
    const onKeyDown = (event) => {
        if (liveHref || event.key !== "Enter") return;
        event.preventDefault();
        discloseOrOpen();
    };
    const confirm = () => {
        if (remember && origin) rememberRenderOrigin(origin);
        setPending(false);
        openRenderDestination(destination);
    };

    const dialog =
        pending && typeof globalThis.document !== "undefined"
            ? createPortal(
                  <OverlayDialog
                      open
                      onClose={() => setPending(false)}
                      analyticsId={DIALOG.EXTERNAL_LINK}
                      eyebrow="External destination"
                      title="Open this link?"
                      width="560px"
                  >
                      <div className="grid gap-[14px]">
                          <p className="m-0 text-[12px] leading-[1.5] text-[color:var(--muted)]">
                              This destination is outside the dashboard. Review the full URL before continuing.
                          </p>
                          <code
                              data-render-link-destination
                              className="block max-h-[180px] overflow-auto whitespace-pre-wrap break-all border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px] text-[11px] leading-[1.45] text-[color:var(--ink)]"
                          >
                              {destination}
                          </code>
                          {origin ? (
                              <label className="flex cursor-pointer items-start gap-[8px] text-[11px] leading-[1.4] text-[color:var(--muted)]">
                                  <input
                                      type="checkbox"
                                      checked={remember}
                                      onChange={(event) => setRemember(event.target.checked)}
                                      className="mt-[2px]"
                                  />
                                  <span>Don&apos;t ask again for {origin} in this browser.</span>
                              </label>
                          ) : (
                              <p className="m-0 text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
                                  Email links are confirmed every time.
                              </p>
                          )}
                          <div className="flex justify-end gap-[8px]">
                              <button
                                  type="button"
                                  onClick={() => setPending(false)}
                                  className="cursor-pointer border border-[color:var(--line)] px-[10px] py-[5px] text-[10px] uppercase tracking-[0.1em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
                              >
                                  Cancel
                              </button>
                              <button
                                  type="button"
                                  onClick={confirm}
                                  className="cursor-pointer border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] py-[5px] text-[10px] uppercase tracking-[0.1em] text-[color:var(--ink)] hover:bg-[color:var(--green)] hover:text-[color:var(--bg)]"
                              >
                                  Open destination
                              </button>
                          </div>
                      </div>
                  </OverlayDialog>,
                  globalThis.document.body,
              )
            : null;

    return (
        <>
            <a
                href={liveHref}
                target={liveHref ? "_blank" : undefined}
                rel={liveHref ? "noopener noreferrer" : undefined}
                role={liveHref ? undefined : "link"}
                tabIndex={liveHref ? undefined : 0}
                aria-haspopup={liveHref ? undefined : "dialog"}
                onClick={onClick}
                onAuxClick={onAuxClick}
                onKeyDown={onKeyDown}
                className={LINK_CLASS}
            >
                {children}
            </a>
            {dialog}
        </>
    );
}
